import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, MapPin } from "lucide-react";
import { fmtTime } from "@/lib/format";

/**
 * Inline room picker for the "Add rehearsal" dialog.
 *
 * Lists every Space across every Facility and, whenever a room + date + time
 * range is chosen, calls /api/facilities/check-conflict to warn the user
 * before they submit. The warning is a visual signal only — the create/patch
 * endpoint enforces the same rule server-side and returns 409 with
 * ``override_conflict`` as the escape hatch.
 */
export default function RoomPicker({ value, onChange, date, startTime, endTime, excludeRehearsalId }) {
  const [facilities, setFacilities] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [f, s] = await Promise.all([api.get("/facilities"), api.get("/spaces")]);
        if (!cancelled) {
          setFacilities(f.data);
          setSpaces(s.data);
        }
      } catch { /* no-op */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const facById = useMemo(() => Object.fromEntries(facilities.map((f) => [f.id, f])), [facilities]);

  useEffect(() => {
    if (!value || !date || !startTime || !endTime) { setConflicts([]); return; }
    let cancelled = false;
    setChecking(true);
    api.post("/facilities/check-conflict", {
      space_id: value, date, start_time: startTime, end_time: endTime,
      exclude_rehearsal_id: excludeRehearsalId || null,
    }).then((r) => { if (!cancelled) setConflicts(r.data?.conflicts || []); })
      .catch(() => { if (!cancelled) setConflicts([]); })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [value, date, startTime, endTime, excludeRehearsalId]);

  if (spaces.length === 0) {
    return (
      <div className="text-[11px] text-zinc-500 italic">
        No rooms defined yet.{" "}
        <a href="/facilities" className="text-gold underline">Set up Facilities</a> to enable double-booking prevention.
      </div>
    );
  }

  return (
    <div>
      <select
        data-testid="rehearsal-space-select"
        value={value || ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="w-full h-9 rounded-md bg-[#0A0A0A] border border-white/10 text-sm px-2"
      >
        <option value="">— No room / off-site —</option>
        {spaces.map((s) => (
          <option key={s.id} value={s.id}>
            {facById[s.facility_id]?.name || "Facility"} · {s.name}{s.capacity ? ` (${s.capacity})` : ""}
          </option>
        ))}
      </select>
      {value && conflicts.length > 0 && (
        <div className="mt-2 rounded-md border border-red-500/40 bg-red-500/10 p-2 text-[11px] text-red-200" data-testid="rehearsal-space-conflict">
          <div className="flex items-center gap-1 font-semibold">
            <AlertTriangle className="w-3.5 h-3.5" /> Room already booked at this time
          </div>
          <ul className="mt-1 space-y-0.5 pl-4 list-disc">
            {conflicts.map((c) => (
              <li key={c.rehearsal_id}>
                {c.title} · {fmtTime(c.start_time)}–{fmtTime(c.end_time)}
                {c.production_title && <span className="text-red-300/70"> · {c.production_title}</span>}
              </li>
            ))}
          </ul>
          <div className="mt-1 text-red-300/70">Change the time or pick another room. Saving anyway requires admin override.</div>
        </div>
      )}
      {value && !checking && conflicts.length === 0 && date && startTime && endTime && (
        <div className="mt-1 text-[10px] text-green-500 flex items-center gap-1">
          <MapPin className="w-3 h-3" /> Room is free.
        </div>
      )}
    </div>
  );
}
